import moment from "moment";
import {
  CommandBar,
  Dropdown,
  DropdownMenuItemType,
  IDropdownOption,
  ITag,
  TagPicker,
} from "office-ui-fabric-react";
import "./assign-command-bar.css";
import React from "react";
import { useSelector } from "react-redux";
import { jobsSelector } from "../../../../store/jobs.store";
import { AssignGroupingKey, Job, KeyValue } from "../../../../types/types";
import { nenkiList } from "../../../../util/util";
import { assignListGrid } from "../assign-list/AssignList";
import * as wjGrid from "@grapecity/wijmo.grid";
import * as wjcGridXlsx from "@grapecity/wijmo.grid.xlsx";

/**
 * アサイン画面コマンドバーProps
 */
export interface AssignCommandBarProps {
  nenki: string;
  selectedGroupingCondition: AssignGroupingKey;
  textSearchConditions: KeyValue[];
  textSearchSuggetions: KeyValue[];
  isOnline: boolean;
  onNenkiChanged: (nenki: string) => void;
  onJobCreateButtonClicked: () => void;
  onJobUpdateButtonClicked: () => void;
  onJobDeleteButtonClicked: () => void;
  USER_INSERT_ASSIGN: () => void;
  USER_DELETE_ASSIGN: () => void;
  USER_RELOAD_ASSIGNS_LIST: () => void;
  USER_RESET_ASSIGNS_LIST: () => void;
  onAssignSaveButtonClick: () => void;
  onGroupingConditionChanged: (key: AssignGroupingKey) => void;
  onRegisterCustomFilterButtonClick: () => void;
  onFilterManagementButtonClick: () => void;
  onTextSearchConditionChanged: (conditions: KeyValue[]) => void;
  onOpenChangeHistoryButtonClick: () => void;
}

/**
 * アサイン画面コマンドバー
 * @param props
 */
export const AssignCommandBar = (props: AssignCommandBarProps) => {
  const jobs = useSelector(jobsSelector);

  return (
    <CommandBar
      items={getCommandBarItems(props, jobs)}
      farItems={getCommandBarFarItems(props)}
      styles={{
        root: {
          alignItems: "center",
        },
      }}
    />
  );
};

/**
 * コマンドバー表示アイテム定義(左側)
 * @param props
 * @param jobs
 */
const getCommandBarItems = (props: AssignCommandBarProps, jobs: Job[]) => {
  const nenkiOption = nenkiList.map((nenki) => ({ key: nenki, text: nenki }));

  const onNenkiChanged = (
    e: React.FormEvent<HTMLDivElement>,
    item: IDropdownOption
  ) => {
    props.onNenkiChanged(String(item.key));
  };

  const onClickSyncBoardProjectListButton = () => {
    fetch("/Board/SyncBoardProjectList").then(async (response) => {
      if (response.status === 200) {
        alert("board案件リスト同期成功");
      } else {
        const error = await response.text();
        console.error("board案件リスト同期失敗");
        console.log(error);
        alert("board案件リスト同期失敗\n\n" + error);
      }
    });
  };

  return [
    {
      key: "nenki",
      onRender: () => (
        <Dropdown
          defaultSelectedKey={props.nenki}
          onChange={onNenkiChanged as any}
          options={nenkiOption}
          styles={{ dropdown: { width: 80 } }}
        />
      ),
    },
    {
      key: "share",
      name: "保存",
      iconProps: {
        iconName: "OfflineStorageSolid",
      },
      disabled: !props.isOnline,
      onClick: () => props.onAssignSaveButtonClick(),
      split: true,
      subMenuProps: {
        items: [
          {
            key: "exportToExcel",
            name: "Excelにエクスポート",
            iconProps: {
              iconName: "ExcelLogoInverse",
            },
            onClick: () => {
              wjcGridXlsx.FlexGridXlsxConverter.saveAsync(
                assignListGrid.ref,
                {
                  // includeColumnHeaders: true,
                  // includeCellStyles: true,
                  formatItem: exportFormatItem(jobs),
                },
                `assign_${moment(Date.now()).format("YYYYMMDD")}.xlsx`
              );
            },
          },
        ],
      },
    },
    {
      key: "insertLine",
      name: "行挿入",
      iconProps: {
        iconName: "CircleAddition",
      },
      onClick: () => props.USER_INSERT_ASSIGN(),
    },
    {
      key: "delteLine",
      name: "行削除",
      iconProps: {
        iconName: "Blocked2",
      },
      onClick: () => props.USER_DELETE_ASSIGN(),
      split: true,
    },
    {
      key: "expandAllGroups",
      name: "展開",
      iconProps: {
        iconName: "Move",
      },
      onClick: () => assignListGrid.expandAll(),
    },
    {
      key: "collapseAllGroups",
      name: "折り畳む",
      iconProps: {
        iconName: "BackToWindow",
      },
      onClick: () => assignListGrid.collapseAll(),
    },
    // {
    //   key: "job",
    //   name: "ジョブ",
    //   iconProps: {
    //     iconName: "DataConnectionLibrary"
    //   },
    //   disabled: !props.isOnline,
    //   subMenuProps: {
    //     items: [
    //       {
    //         key: "addNewJob",
    //         name: "新規ジョブ登録",
    //         iconProps: {
    //           iconName: "CircleAddition"
    //         },
    //         onClick: () => props.onJobCreateButtonClicked()
    //       },
    //       {
    //         key: "editJob",
    //         name: "選択されているジョブを編集",
    //         iconProps: {
    //           iconName: "EditMirrored"
    //         },
    //         onClick: () => props.onJobUpdateButtonClicked()
    //       },
    //       {
    //         key: "deleteJob",
    //         name: "選択されているジョブを削除",
    //         iconProps: {
    //           iconName: "Blocked2"
    //         },
    //         onClick: () => props.onJobDeleteButtonClicked()
    //       }
    //     ]
    //   }
    // },
    {
      key: "forManager",
      name: "管理職用機能",
      iconProps: {
        iconName: "ReportHacked",
      },
      subMenuProps: {
        items: [
          {
            key: "syncBoardProjectList",
            name: "board案件リスト同期",
            iconProps: {
              iconName: "Sync",
            },
            onClick: () => onClickSyncBoardProjectListButton(),
          },
        ],
      },
    },
  ];
};

/**
 * コマンドバー表示アイテム定義(右側)
 * @param props
 */
const getCommandBarFarItems = (props: AssignCommandBarProps) => {
  const groupingKeys: Array<{ key: AssignGroupingKey; text: string }> = [
    { key: "jobCode", text: "ジョブ別" },
    { key: "inChargeKojinCode", text: "担当別" },
  ];

  const onGroupingConditionChanged = (
    e: React.FormEvent<HTMLDivElement>,
    item?: IDropdownOption
  ) => {
    if (item) {
      props.onGroupingConditionChanged(item.key as AssignGroupingKey);
    }
  };

  const defaultItems: ITag[] = props.textSearchConditions.map((v) => ({
    key: v.key,
    name: v.value,
  }));

  const suggestions: ITag[] = props.textSearchSuggetions.map((item) => ({
    key: item.key,
    name: item.value,
  }));

  const listContainsDocument = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return (
      tagList.filter((compareTag) => compareTag.key === tag.key).length > 0
    );
  };

  const onFilterChanged = (filterText: string, tagList?: ITag[]): ITag[] => {
    return filterText
      ? suggestions
          .filter(
            (tag) =>
              tag.key
                .toString()
                .toLowerCase()
                .indexOf(filterText.toLowerCase()) === 0 ||
              tag.name.toLowerCase().indexOf(filterText.toLowerCase()) >= 0
          )
          .filter((tag) => !listContainsDocument(tag, tagList))
      : [];
  };

  const getTextFromItem = (item: ITag): string => {
    return item.name;
  };

  const onItemChanged = (items?: ITag[]) => {
    const conditions = items
      ? items.map((v) => ({ key: v.key, value: v.name }))
      : [];
    props.onTextSearchConditionChanged(conditions as any); // FIXME
  };

  // noinspection JSUnusedGlobalSymbols
  return [
    {
      key: "view",
      iconProps: {
        iconName: "View", // RedEye View
      },
      subMenuProps: {
        items: [
          {
            key: "reload",
            name: "再読込",
            iconProps: {
              iconName: "Refresh",
            },
            onClick: () => props.USER_RELOAD_ASSIGNS_LIST(),
          },
          {
            key: "clearGridState",
            name: "一覧の状態をクリア",
            iconProps: {
              iconName: "ClearFilter",
            },
            onClick: () => alert("未実装"),
          },
          {
            key: "clearGridStateWithRefresh",
            name: "一覧の状態をクリアして再読込",
            iconProps: {
              iconName: "FullHistory",
            },
            onClick: () => props.USER_RESET_ASSIGNS_LIST(),
          },
        ],
      },
    },
    {
      key: "grouping",
      onRender: () => (
        <Dropdown
          defaultSelectedKey={props.selectedGroupingCondition}
          onChange={onGroupingConditionChanged}
          options={[
            {
              key: "header",
              text: "グルーピング",
              itemType: DropdownMenuItemType.Header,
            },
            ...groupingKeys,
          ]}
          styles={{ dropdown: { width: 100 } }}
        />
      ),
    },
    {
      key: "searchIcon",
      iconProps: {
        iconName: "Search",
      },
      subMenuProps: {
        items: [
          {
            key: "addFilter",
            name: "現在の検索条件をカスタムフィルターとして登録",
            iconProps: {
              iconName: "Filter",
            },
            onClick: () => props.onRegisterCustomFilterButtonClick(),
          },
          {
            key: "filterSettings",
            name: "カスタムフィルター管理",
            iconProps: {
              iconName: "Settings",
            },
            onClick: () => props.onFilterManagementButtonClick(),
          },
        ],
      },
    },
    {
      key: "search",
      name: "検索",
      // https://github.com/OfficeDev/office-ui-fabric-react/issues/5660
      onRender: () => (
        <TagPicker
          onResolveSuggestions={onFilterChanged}
          getTextFromItem={getTextFromItem}
          onChange={onItemChanged}
          defaultSelectedItems={defaultItems}
          pickerSuggestionsProps={{
            suggestionsHeaderText: "こちらをお探しですか？",
            noResultsFoundText: "見つかりませんでした",
          }}
          styles={{ root: { width: "100%" } }}
        />
      ),
    },
  ];
};

const exportFormatItem = (jobs: Job[]) => (args: any) => {
  const p = args.panel,
    row = args.row,
    col = args.col,
    xlsxCell = args.xlsxCell;
  if (
    p.cellType === wjGrid.CellType.Cell &&
    col === 0 &&
    p.rows[row] instanceof wjGrid.GroupRow
  ) {
    const jobCode = xlsxCell.value;
    const job = jobs.find((job) => job.jobNo === Number(jobCode));
    xlsxCell.value = `（${jobCode}）${job?.jobName}`;
  }
};
